/**
 * Tracks app load lifecycle events (TTI - Time to Interactive)
 */

import mixpanelService from '../mixpanel-service'
import { generateUUID } from '@utils/uuid'
import { EVENTS, PROPS, LAUNCH_TYPES, START_REASONS } from './analytics-spec'

class AppLoadTracker {
    constructor() {
        if (AppLoadTracker.instance) {
            return AppLoadTracker.instance
        }
        AppLoadTracker.instance = this

        this._appLoadId = null
        this._startTime = null
        this._started = false
        this._ready = false
        this._launchType = null
        this._startReason = null
    }

    /**
     * Start app load tracking - fires app_load_started event
     */
    start() {
        if (this._started) return

        this._started = true
        this._startTime = performance.now()
        this._appLoadId = generateUUID()
        this._launchType = this._getLaunchType()
        this._startReason = this._getStartReason(this._launchType)

        mixpanelService.track(EVENTS.APP_LOAD_STARTED, {
            [PROPS.APP_LOAD_ID]: this._appLoadId,
            [PROPS.LAUNCH_TYPE]: this._launchType,
            [PROPS.START_REASON]: this._startReason
        })
    }

    /**
     * Mark app as ready - fires app_ready event with tti_ms
     */
    markReady() {
        if (!this._started || this._ready) return

        this._ready = true
        const ttiMs = Math.round(performance.now() - this._startTime)

        mixpanelService.track(EVENTS.APP_READY, {
            [PROPS.APP_LOAD_ID]: this._appLoadId,
            [PROPS.TTI_MS]: ttiMs,
            [PROPS.TT_APP_READY_MS]: ttiMs,  // Backward compatibility
            [PROPS.READY_STATE]: 'shell_ready',
            [PROPS.LAUNCH_TYPE]: this._launchType,
            [PROPS.START_REASON]: this._startReason
        })
    }

    _getLaunchType() {
        try {
            const entries = performance.getEntriesByType('navigation')
            if (entries && entries.length > 0) {
                const navType = entries[0].type
                if (navType === 'reload') return LAUNCH_TYPES.RELOAD
                if (navType === 'back_forward') return LAUNCH_TYPES.BACK_FORWARD
            }
        } catch (e) {
            // Navigation Timing API not supported
        }
        return LAUNCH_TYPES.NAVIGATE
    }

    /**
     * Determine the reason for app load (web equivalent of iOS start reasons)
     * @param {string} launchType - The launch type from Navigation Timing API
     * @returns {string} The start reason
     */
    _getStartReason(launchType) {
        // Map launch type to start reason for reload and back_forward
        if (launchType === LAUNCH_TYPES.RELOAD) {
            return START_REASONS.REFRESH
        }
        if (launchType === LAUNCH_TYPES.BACK_FORWARD) {
            return START_REASONS.BACK_FORWARD
        }

        // For navigate type, check if it's a deeplink (specific content route)
        try {
            const path = window.location.pathname
            // Deeplink patterns: direct links to content
            const deeplinkPatterns = [
                /^\/mix\//,           // /mix/:id
                /^\/artist\//,        // /artist/:url
                /^\/channel\//,       // /channel/:id
                /^\/browse\/mixes\//, // /browse/mixes/:id
                /^\/radio\//,         // /radio/:id
                /^\/search/           // /search?q=...
            ]
            if (deeplinkPatterns.some(pattern => pattern.test(path))) {
                return START_REASONS.DEEPLINK
            }
        } catch (e) {
            // Location API not available
        }

        // Default: fresh navigation to home or other non-content pages
        return START_REASONS.COLD_START
    }

    getAppLoadId() {
        return this._appLoadId
    }

    getStartTime() {
        return this._startTime
    }
}

export default new AppLoadTracker()
